import { findGroup, locateRow } from "../find";
import { isRow } from "../sections";
import type { EmailDocument } from "../types";
import {
  emptyRowMultiSelection,
  getPrimaryRowId,
  rowMultiSelectionFromRowId,
  type RowMultiSelection,
} from "./row-multi-selection";

export type RowSiblingList =
  | { parent: "document"; rowIds: string[] }
  | { parent: "group"; groupId: string; rowIds: string[] };

function orderIdsInSiblingList(rowIds: string[], siblingRowIds: string[]): string[] {
  const selected = new Set(rowIds);
  return siblingRowIds.filter(id => selected.has(id));
}

export function siblingListsEqual(
  left: RowSiblingList | null,
  right: RowSiblingList | null,
): boolean {
  if (left === null || right === null) {
    return false;
  }
  if (left.parent !== right.parent) {
    return false;
  }
  if (left.parent === "group" && right.parent === "group") {
    return left.groupId === right.groupId;
  }
  return true;
}

export function resolveTopLevelRowIds(document: EmailDocument): string[] {
  const rowIds: string[] = [];
  for (const section of document.sections) {
    if (isRow(section)) {
      rowIds.push(section.id);
    }
  }
  return rowIds;
}

export function resolveRowSiblingList(
  document: EmailDocument,
  rowId: string,
): RowSiblingList | null {
  const located = locateRow(document.sections, rowId);
  if (!located) {
    return null;
  }

  if (located.location.parent === "document") {
    return { parent: "document", rowIds: resolveTopLevelRowIds(document) };
  }

  const group = findGroup(document.sections, located.location.groupId);
  if (!group) {
    return null;
  }

  return {
    parent: "group",
    groupId: group.id,
    rowIds: group.rows.map(row => row.id),
  };
}

export function resolveRowSiblingListForSelection(
  document: EmailDocument,
  selection: Pick<RowMultiSelection, "selectedRowIds" | "primaryRowId">,
): RowSiblingList | null {
  const primary = getPrimaryRowId(selection);
  if (primary !== null) {
    return resolveRowSiblingList(document, primary);
  }

  const first = selection.selectedRowIds[0];
  if (first === undefined) {
    return null;
  }

  return resolveRowSiblingList(document, first);
}

export function applyRowSelectionClick(args: {
  document: EmailDocument;
  current: RowMultiSelection;
  rowId: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
}): RowMultiSelection {
  const { document, current, rowId, metaKey, ctrlKey, shiftKey } = args;
  const targetList = resolveRowSiblingList(document, rowId);
  if (targetList === null) {
    return rowMultiSelectionFromRowId(rowId);
  }

  const currentList = resolveRowSiblingListForSelection(document, current);
  const additive = metaKey || ctrlKey;

  if (
    shiftKey &&
    !additive &&
    currentList !== null &&
    siblingListsEqual(currentList, targetList)
  ) {
    const anchorId =
      current.anchorRowId !== null && targetList.rowIds.includes(current.anchorRowId)
        ? current.anchorRowId
        : getPrimaryRowId(current) ?? rowId;
    const anchorIndex = targetList.rowIds.indexOf(anchorId);
    const targetIndex = targetList.rowIds.indexOf(rowId);
    if (anchorIndex < 0 || targetIndex < 0) {
      return rowMultiSelectionFromRowId(rowId);
    }

    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    return {
      selectedRowIds: targetList.rowIds.slice(start, end + 1),
      primaryRowId: rowId,
      anchorRowId: anchorId,
    };
  }

  if (additive) {
    if (currentList === null || !siblingListsEqual(currentList, targetList)) {
      return rowMultiSelectionFromRowId(rowId);
    }

    const alreadySelected = current.selectedRowIds.includes(rowId);
    if (alreadySelected) {
      const nextIds = current.selectedRowIds.filter(id => id !== rowId);
      if (nextIds.length === 0) {
        return emptyRowMultiSelection();
      }

      const ordered = orderIdsInSiblingList(nextIds, targetList.rowIds);
      const primaryRowId =
        current.primaryRowId !== null && ordered.includes(current.primaryRowId)
          ? current.primaryRowId
          : ordered[ordered.length - 1] ?? null;

      return {
        selectedRowIds: ordered,
        primaryRowId,
        anchorRowId: rowId,
      };
    }

    const ordered = orderIdsInSiblingList(
      [...current.selectedRowIds, rowId],
      targetList.rowIds,
    );
    return {
      selectedRowIds: ordered,
      primaryRowId: rowId,
      anchorRowId: rowId,
    };
  }

  return rowMultiSelectionFromRowId(rowId);
}
